import React, { useCallback, useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { flexiBenefitsService } from "../../../services/api";
import { formatInr } from "./runPayrollUtils";

/**
 * Run Payroll → Payslips step → Reimbursement tab.
 * Lists approved Flexi claims ready for / already in pay month.
 */
export default function PayrollReimbursementPayslipsPanel({
  darkMode = false,
  payMonth,
  finalized = false,
  processedEmployees = [],
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const border = "border-[hsl(var(--ds-border))]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";
  const card = darkMode ? "bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-surface))]";

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const ids = (processedEmployees || []).map((e) => e.id || e.employeeId).filter(Boolean);
      const res = await flexiBenefitsService.listPayrollPostings({
        payMonth,
        employeeIds: ids.length ? ids.join(",") : undefined,
      });
      setRows(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load reimbursement claims");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [payMonth, processedEmployees]);

  useEffect(() => {
    load();
  }, [load]);

  const markPaid = async () => {
    const ids = rows.filter((r) => r.status !== "PAID").map((r) => r.id);
    if (!ids.length) {
      toast("Nothing to mark as paid");
      return;
    }
    setBusy(true);
    try {
      await flexiBenefitsService.markPaid({ claimIds: ids, payMonth });
      toast.success(`Marked ${ids.length} claim(s) paid for ${payMonth}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Mark paid failed");
    } finally {
      setBusy(false);
    }
  };

  if (!finalized) {
    return (
      <div className={`rounded-xl border px-4 py-3 text-sm ${border} ${muted}`}>
        Finalise payroll first to publish / settle reimbursement payslips for this month.
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex h-24 items-center justify-center gap-2 text-sm ${muted}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={16} />
        Loading reimbursement claims…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className={`rounded-xl border ${border} ${card}`}>
        <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${border}`}>
          <div>
            <p className="text-sm font-semibold">Reimbursement / Flexi claims</p>
            <p className={`text-[11px] ${muted}`}>
              Approved Flexi claims for {payMonth}. Method A pays after approval; Method B shows tax
              split on amounts already paid via structure.
            </p>
          </div>
          <button
            type="button"
            disabled={busy || !rows.some((r) => r.status !== "PAID")}
            onClick={markPaid}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Mark paid in payroll
          </button>
        </div>

        {!rows.length ? (
          <div className={`px-4 py-8 text-center text-sm ${muted}`}>
            No approved Flexi reimbursement claims for this month.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-xs">
              <thead>
                <tr className={`border-b text-left ${border} ${muted}`}>
                  <th className="px-3 py-2 font-semibold">Emp</th>
                  <th className="px-3 py-2 font-semibold">Component</th>
                  <th className="px-3 py-2 font-semibold">Method</th>
                  <th className="px-3 py-2 text-right font-semibold">Claim</th>
                  <th className="px-3 py-2 text-right font-semibold">Approved</th>
                  <th className="px-3 py-2 text-right font-semibold">Payroll</th>
                  <th className="px-3 py-2 text-right font-semibold">Exempt</th>
                  <th className="px-3 py-2 text-right font-semibold">Taxable</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b ${border}`}>
                    <td className="px-3 py-2 tabular-nums">{r.employeeId}</td>
                    <td className="px-3 py-2 font-medium">{r.componentCode}</td>
                    <td className="px-3 py-2">
                      {r.payMethod === "CLAIM_THEN_COLLECT" || r.payMethod === "CLAIM_THEN_COLLECT"
                        ? "B · Pay then bills"
                        : "A · Bills then pay"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInr(r.claimAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInr(r.approvedAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInr(r.payrollAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                      {formatInr(r.exemptAmount)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                      {formatInr(r.taxableAmount)}
                    </td>
                    <td className="px-3 py-2">
                      {r.status === "PAID" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 size={12} /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <XCircle size={12} /> {r.status}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
